import { addListener } from "../event/private.js";
import type {
  DispatchOptions,
  DispatchParameters,
  NexusSignalAssignable,
  NexusSignal,
} from "../event/event.types.js";
/*                                       */
import { assignSignal, assignFunction, assignNamespace } from "../store/assign.js";
import type { AnyFunction } from "../utils/utils.types.js";
import type { GetterHandler, NexusEventTarget, NexusProxy } from "../store/store.types.js";
import { withOptions } from "./promise.js";
import type { AsyncNamespace } from "../namespace/namespace.types.js";
import { assertNamespaceType, storeFlags } from "../utils/utils.js";
import { getStoreType } from "../store/utils.js";
import { setupEventTarget } from "../store/store.events.js";
import type { NexusResult, QueuedFunction } from "../function/function.types.js";
import { createProxy } from "./proxy.js";
import { configSync } from "./proxyConfig.js";

/**
 *
 */
export const applyHandler: GetterHandler<(thisArg: unknown, args: unknown[]) => unknown> =
  (store, _, config) => (thisArg, argArray) => {
    const [, dispatchEvent, invokeQueueHandler] = config;

    /*                                                 */
    /*                                */
    if (store.isSig) return dispatchEvent(store, argArray as never);

    /*                                                                           */
    assertNamespaceType(store, storeFlags[2], "invoke");
    return invokeQueueHandler(thisArg, store, argArray);
  };

/**
 *
 */
export const withHandler: GetterHandler<NexusResult<unknown>["with"]> = (store) => (options) =>
  withOptions(store.ready, store, options);

/**
 *
 */
export const callHandler: GetterHandler<AnyFunction> =
  (store, target, config) =>
  (thisArg, ...argArray) =>
    applyHandler(store, target, config)(thisArg, argArray);

/**
 *
 */
export const isSetHandler: GetterHandler<() => NexusProxy["isSet"]> = (_, target) => target.isSet;

/**
 *
 */
export const thenHandler: GetterHandler<NexusResult<unknown>["then"]> = (store) => (callback) =>
  store.ready.then(callback) as NexusResult<unknown>;

/**
 *
 */
export const subscribeHandler: GetterHandler<NexusSignal<unknown>["subscribe"]> =
  (store) => (listener, options) =>
    addListener(store, listener, options);

/**
 *
 */
export const dispatchHandler: GetterHandler<NexusSignal<unknown>["dispatch"]> =
  (store, _, config) =>
  (...args: DispatchParameters<unknown>) =>
    config[1](store, args);

/**
 *
 */
export const setEventValueHandler: GetterHandler<NexusSignal<unknown>["set"]> =
  (store, _, config) =>
  (...args: DispatchParameters<NonNullable<unknown>>) =>
    config[1](store, [args[0], { ...args[1], retain: true } as DispatchOptions]);

/**
 *
 */
export const updateEventValueHandler: GetterHandler<NexusSignal<unknown>["update"]> =
  (store, _, config) => (updater) =>
    config[1](store, [updater(store.detail), { retain: true }]);

/**
 *
 */
export const getEventValueHandler: GetterHandler<NexusSignal<unknown>["get"]> = (store) => () => {
  if (store.isNs && store.isReady) {
    return Object.fromEntries(
      Object.entries(store.members).map(([key, value]) => {
        if (value.handler) return [key, value.handler];
        return [key, createProxy(value, configSync)];
      }),
    ) as NonNullable<unknown>;
  }

  return store.detail;
};

/**
 *
 */
export const toStringHandler: GetterHandler<object["toString"]> = (store, target) => () => {
  const members = Object.keys(store.members);
  const { isSig, isNs, isFunc, isReady } = store;
  const isWrapper = !isSig && !isFunc && !isNs && !isSig;

  const desc = [`[${getStoreType(store)}: ${(store.handler ?? target).name}`];

  if (!isWrapper) desc.push(" (");
  if (isSig) desc.push(`listeners: ${store.listeners.length}`);
  if (isFunc) desc.push(`pending invocations: ${store.invocations.length}, isSet: ${target.isSet}`);
  if (isNs) desc.push(`members: ${members.join()}, ready: ${!!isReady}`);
  if (!isWrapper) desc.push(")");

  desc.push("]");
  return desc.join("");
};

/**
 *
 */
export const assignEventHandler: GetterHandler<NexusSignalAssignable<unknown>["assignEvent"]> =
  (store, target) => (listeners, options?) =>
    assignSignal(store, target, { listeners }, options);

/**
 *
 */
export const assignStoreHandler: GetterHandler<NexusSignalAssignable<unknown>["assignStore"]> =
  (store, target) => (detail, start, options) =>
    assignSignal(store, target, { detail, start }, options);

/**
 *
 */
export const assignNamespaceHandler: GetterHandler<
  AsyncNamespace<Record<string, unknown>>["assignNamespace"]
> = (store, target) => (value, options?) => assignNamespace(store, target, value, options);

/**
 *
 */
export const assignFunctionHandler: GetterHandler<QueuedFunction<AnyFunction>["assignFunction"]> =
  (store, target) => (value, options) =>
    assignFunction(store, target, value, options);

/**
 *
 */
export const addEventListenerHandler: GetterHandler<NexusEventTarget["addEventListener"]> =
  (store) => (type: string, listener: AnyFunction, options?: boolean | AddEventListenerOptions) =>
    setupEventTarget(store)?.addEventListener(type, listener, options);

/**
 *
 */
export const removeEventListenerHandler: GetterHandler<NexusEventTarget["removeEventListener"]> =
  (store) => (type: string, listener: AnyFunction, options?: boolean | AddEventListenerOptions) =>
    store.eventTarget?.removeEventListener(type, listener, options);
